import { ComputedRef } from 'vue'

import { Course } from '@/domain/entities/problem/course'
import { CourseSolutionRecord } from '@/domain/entities/problem/courseSolutionRecord'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'
import { ProgramLanguageType } from '@/domain/value_objects/language/programLanguageType'
import { ArenaViewModel } from '@/presentation/viewmodels/arena/arenaViewModel'
import { CourseHeatMapViewModel } from '@/presentation/viewmodels/dashboard/courseHeatMapViewModel'
import { CourseViewModel } from '@/presentation/viewmodels/problem/courseViewModel'
import { ProblemStateViewModel } from '@/presentation/viewmodels/problem/problemStateViewModel'
import { getCourseHeatMapOptions } from '@/util/dashboard/courseHeatMapOptions'

export class CourseHeatMapPresenter {
  private viewModel: CourseHeatMapViewModel
  private arenaViewModel: ArenaViewModel
  private problemStateViewModel: ProblemStateViewModel
  private courseViewModel: CourseViewModel
  private isFirstTimeAllLanguage = true

  constructor(
    viewModel: CourseHeatMapViewModel,
    arenaViewModel: ArenaViewModel = new ArenaViewModel(),
    problemStateViewModel: ProblemStateViewModel = new ProblemStateViewModel(),
    courseViewModel: CourseViewModel = new CourseViewModel()
  ) {
    this.viewModel = viewModel
    this.arenaViewModel = arenaViewModel
    this.problemStateViewModel = problemStateViewModel
    this.courseViewModel = courseViewModel
  }

  get courseStatisticsList(): ComputedRef<{ [courseName: string]: { [programLanguage: string]: Course } }> {
    return this.viewModel.courseStatisticsList
  }

  get heatMapXLabel(): ComputedRef<string[]> {
    return this.viewModel.heatMapXLabel
  }

  get heatMapSeries(): ComputedRef<any[]> {
    return this.viewModel.heatMapSeries
  }

  get chartOptions(): ComputedRef<any> {
    return this.viewModel.chartOptions
  }

  get hasData(): ComputedRef<boolean> {
    return this.viewModel.hasData
  }

  get isPlotting(): ComputedRef<boolean> {
    return this.viewModel.isPlotting
  }

  get isLoading(): ComputedRef<boolean> {
    return this.viewModel.isLoading
  }

  get error(): ComputedRef<string | null> {
    return this.viewModel.error
  }

  async getCourseStatisticsListByLanguage(
    courseName: string,
    userId: string,
    programLanguage: ProgramLanguageType
  ): Promise<void> {
    await this.viewModel.getCourseStatisticsListByLanguage(courseName, userId, programLanguage)
  }

  // ヒートマップを描画する
  plotHeatMap = async (
    course: Course,
    userId: string,
    programLanguage: ProgramLanguageType | 'All',
    displayLanguage: DisplayLanguageType,
    isLightMode: boolean
  ) => {
    if (this.isPlotting.value) return
    this.viewModel.setIsPlotting(true)
    this.viewModel.setHasData(false)

    if (programLanguage == 'All') {
      // すべての言語のヒートマップを描画
      try {
        if (this.isFirstTimeAllLanguage) {
          await this.viewModel.getCourseSolutoinRecordList(userId)
          await this.recordListByTopic(
            this.viewModel.courseSolutionRecordList.value,
            this.getProblemTopicList(this.courseViewModel.courseList.value)
          )
        }
        await this.plotAnyLanguageHeatMap(course)
        this.viewModel.setHasData(true)
        this.isFirstTimeAllLanguage = false
      } catch (e) {
        this.viewModel.setHasData(false)
      }
    } else {
      // 特定の言語のヒートマップを描画
      try {
        await this.viewModel.getCourseStatisticsListByLanguage(course.shortName, userId, programLanguage)
        const trimProgramLanguage = programLanguage.replace(' (Beta)', '').replace('#', '%23')
        const courseByLanguage = this.courseStatisticsList.value[course.shortName][trimProgramLanguage]
        await this.plotProgramLanguageHeatMap(course, courseByLanguage)
      } catch (e) {
        await this.plotEmptyHeatMap(course)
      } finally {
        this.viewModel.setHasData(true)
      }
    }
    await this.viewModel.setChartOptions(
      getCourseHeatMapOptions(this.viewModel.heatMapXLabel.value, isLightMode, displayLanguage)
    )
    this.viewModel.setIsPlotting(false)
  }

  private getProblemTopicList = (courseList: { [id: number]: Course }): string[] => {
    const topicList: string[] = []

    for (const id in courseList) {
      if (Object.prototype.hasOwnProperty.call(courseList, id)) {
        const course = courseList[id]
        topicList.push(course.shortName)
      }
    }
    return topicList
  }

  private recordListByTopic = async (courseSolutionRecordList: CourseSolutionRecord[], topicList: string[]) => {
    const sortedRecordList: { [topic: string]: string[] } = {}

    topicList.forEach(topic => {
      sortedRecordList[topic] = []
    })

    courseSolutionRecordList.forEach(course => {
      for (const topic of topicList) {
        if (course.problemId.startsWith(topic)) {
          sortedRecordList[topic].push(course.problemId)
          break
        }
      }
    })

    for (const topic of topicList) {
      sortedRecordList[topic].sort()
    }

    this.viewModel.setCourseSolutoinRecordListDictionary(sortedRecordList)
  }

  // すべての言語のヒートマップを描画する
  private plotAnyLanguageHeatMap = async (course: Course) => {
    const series: any[] = []
    const xlabel: string[] = []
    let max = 0

    if (course.topics != undefined) {
      for (let i = course.topics.length - 1; i >= 0; i--) {
        const topic = course.topics[i]
        const data = []

        for (let j = 0; j < topic.problems.length; j++) {
          const problem = topic.problems[j]
          if (this.viewModel.courseSolutionRecordListDictionary.value[course.shortName].includes(problem.id)) {
            data.push(1)
          } else {
            data.push(0)
          }
        }
        series.push({ name: topic.shortName, data: data })

        max = Math.max(max, topic.problems.length)
      }

      for (let i = 0; i < course.topics.length; i++) {
        const add = max - series[i].data.length
        for (let j = 0; j < add; j++) {
          series[i].data.push(-1)
        }
      }

      for (let i = 0; i < max; i++) {
        xlabel.push(this.numberToLetter(i + 1))
      }

      this.viewModel.setHeatMapSeries(series)
      this.viewModel.setHeatMapXLabel(xlabel)
    }
  }

  // 特定の言語のヒートマップを描画する
  private plotProgramLanguageHeatMap = async (course: Course, courseByLanguage: Course) => {
    const series: any[] = []
    const xlabel: string[] = []
    let max = 0

    if (course.topics != undefined) {
      for (let i = course.topics.length - 1; i >= 0; i--) {
        const topic = course.topics[i]
        const data = []

        if (courseByLanguage == undefined || courseByLanguage.topics == undefined) {
          for (let j = 0; j < topic.problems.length; j++) {
            data.push(0)
          }
        } else {
          const courseByLangTopic = courseByLanguage.topics.find(t => t.shortName === topic.shortName)

          for (let j = 0; j < topic.problems.length; j++) {
            const problem = topic.problems[j]
            if (!problem.isSolved || courseByLangTopic == undefined) data.push(0)
            else {
              const problemIdList = courseByLangTopic.problems as unknown as string[]
              if (problemIdList.includes(problem.id)) {
                data.push(1)
              } else {
                data.push(0)
              }
            }
          }
        }
        series.push({ name: topic.shortName, data: data })

        max = Math.max(max, topic.problems.length)
      }

      for (let i = 0; i < course.topics.length; i++) {
        const add = max - series[i].data.length
        for (let j = 0; j < add; j++) {
          series[i].data.push(-1)
        }
      }
      for (let i = 0; i < max; i++) {
        xlabel.push(this.numberToLetter(i + 1))
      }
      this.viewModel.setHeatMapSeries(series)
      this.viewModel.setHeatMapXLabel(xlabel)
    }
  }

  private plotEmptyHeatMap = async (course: Course) => {
    const series: any[] = []
    const xlabel: string[] = []
    let max = 0

    if (course.topics != undefined) {
      for (let i = course.topics.length - 1; i >= 0; i--) {
        const topic = course.topics[i]
        const data = []

        for (let j = 0; j < topic.problems.length; j++) {
          data.push(0)
        }
        series.push({ name: topic.shortName, data: data })
        max = Math.max(max, topic.problems.length)
      }

      for (let i = 0; i < max; i++) {
        xlabel.push(this.numberToLetter(i + 1))
      }

      for (let i = 0; i < course.topics.length; i++) {
        const add = max - series[i].data.length
        for (let j = 0; j < add; j++) {
          series[i].data.push(-1)
        }
      }

      this.viewModel.setHeatMapSeries(series)
      this.viewModel.setHeatMapXLabel(xlabel)
    }
  }

  private numberToLetter = (num: number) => {
    return String.fromCharCode(64 + num)
  }

  clickElement = (event: any, chartContext: any, opts: any) => {
    const el = event.target
    const seriesIndex = parseInt(el.getAttribute('i'))
    const dataPointIndex = parseInt(el.getAttribute('j'))

    if (isNaN(seriesIndex) || isNaN(dataPointIndex)) return

    const data = parseInt(opts.config.series[seriesIndex].data[dataPointIndex])

    if (data == undefined || data == -1) return

    const seriesName = opts.globals.seriesNames[seriesIndex]
    const dataPointName = opts.globals.labels[dataPointIndex]

    if (seriesName == undefined || dataPointName == undefined) return

    const problemId = seriesName + '_' + dataPointName

    this.arenaViewModel.setArenaProblem('', '', -1)
    this.problemStateViewModel.setProblemId(problemId)
  }
}
